import { Component, ErrorInfo, ReactNode } from 'react';
import { exportBudgetToCSV } from '../utils/exportUtils';
import { getAllBudgets } from '../db';
import { captureException } from '../utils/sentry';

interface ErrorBoundaryProps {
  children: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    // Update state so the next render will show the fallback UI
    return {
      hasError: true,
      error,
      errorInfo: null,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // Log the error to Sentry using our centralized utility
    captureException(error, {
      react: {
        componentStack: errorInfo.componentStack,
      },
    });

    // Also log to console for development
    console.error('Error caught by ErrorBoundary:', error, errorInfo);
    this.setState({
      errorInfo,
    });
  }

  handleExportData = (): void => {
    try {
      const budgets = getAllBudgets();
      if (budgets.length > 0) {
        // Export the most recent budget as a backup
        const mostRecentBudget = budgets.reduce((latest, current) => {
          const latestDate = new Date(latest.date);
          const currentDate = new Date(current.date);
          return currentDate > latestDate ? current : latest;
        });

        exportBudgetToCSV(mostRecentBudget);
      } else {
        alert('No budgets found to export.');
      }
    } catch (error) {
      console.error('Failed to export data:', error);
      alert('Failed to export data. See console for details.');
    }
  };

  handleResetApp = (): void => {
    if (window.confirm('Are you sure you want to reset the application? All data will be lost.')) {
      try {
        localStorage.removeItem('budget_app_data');
        // Reload the page to start fresh
        window.location.reload();
      } catch (error) {
        console.error('Failed to reset application:', error);
        alert('Failed to reset application. See console for details.');
      }
    }
  };

  render(): ReactNode {
    if (this.state.hasError) {
      // Render fallback UI
      return (
        <div className="flex min-h-screen flex-col items-center justify-center bg-slate-50 p-4 text-center dark:bg-surface-950">
          <div className="w-full max-w-md rounded-lg bg-white p-8 shadow-lg dark:bg-surface-900">
            <h2 className="mb-4 text-2xl font-bold text-slate-900 dark:text-white">
              Something went wrong
            </h2>

            <p className="mb-6 text-slate-700 dark:text-slate-300">
              The application encountered an error. This might be due to corrupted data or an
              incompatible data format.
            </p>

            <div className="mb-6 rounded bg-slate-100 p-4 text-left dark:bg-surface-800">
              <p className="mb-2 font-mono text-sm text-slate-800 dark:text-slate-200">
                {this.state.error?.toString()}
              </p>
              {this.state.errorInfo && (
                <details className="mt-2">
                  <summary className="cursor-pointer text-sm text-slate-600 dark:text-slate-400">
                    View technical details
                  </summary>
                  <pre className="mt-2 max-h-60 overflow-auto whitespace-pre-wrap break-words rounded bg-slate-200 p-2 text-xs text-slate-800 dark:bg-surface-700 dark:text-slate-200">
                    {this.state.errorInfo.componentStack}
                  </pre>
                </details>
              )}
            </div>

            <div className="flex flex-col space-y-4">
              <button
                onClick={this.handleExportData}
                className="rounded-md border border-transparent bg-indigo-600 px-4 py-2 text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 dark:bg-indigo-500 dark:text-white dark:hover:bg-indigo-400"
              >
                Export Data Backup
              </button>

              <button
                onClick={this.handleResetApp}
                className="rounded-md border border-slate-500 bg-white px-4 py-2 text-slate-700 hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:border-slate-500 dark:bg-surface-800 dark:text-slate-300 dark:hover:bg-surface-700"
              >
                Reset Application
              </button>

              <button
                onClick={() => window.location.reload()}
                className="rounded-md border border-slate-500 bg-white px-4 py-2 text-slate-700 hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:border-slate-500 dark:bg-surface-800 dark:text-slate-300 dark:hover:bg-surface-700"
              >
                Try Again
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
